import { TemplateFieldType, type TemplateField, type TemplateSchema } from '@/api';
import { evaluateCondition } from '@/composables/contractGeneration/useConditionalVisibility';
import { isJsonEqual } from '@/utils/jsonHelpers';

/**
 * Build a zero-value form state that mirrors the schema.
 *
 * - Required list fields start with one empty item (so users can begin typing immediately).
 * - Optional object fields with a `condition` start as null (user must toggle them on).
 * - Other optional objects start as an empty object.
 * - Primitives start as null / empty string / false.
 */
export function buildDefaultFormData(schema: TemplateSchema): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(schema.fields ?? {})) {
    result[key] = buildDefaultField(field);
  }
  return result;
}

function buildDefaultField(field: TemplateField): unknown {
  switch (field.type) {
    case TemplateFieldType.STRING:
      return '';
    case TemplateFieldType.NUMBER:
      return null;
    case TemplateFieldType.DATE:
      return null;
    case TemplateFieldType.BOOLEAN:
      // Unanswered, not "No": the Yes / No dropdown starts empty, and a
      // required question stays flagged until the person actually picks.
      return null;
    case TemplateFieldType.OBJECT:
    case TemplateFieldType.LEGAL_ENTITY:
    case TemplateFieldType.NATURAL_PERSON: {
      if (field.condition_ast && !field.required) return null;
      const children = field.fields ?? {};
      const obj: Record<string, unknown> = {};
      for (const [childKey, childField] of Object.entries(children)) {
        obj[childKey] = buildDefaultField(childField);
      }
      return obj;
    }
    case TemplateFieldType.LIST: {
      if (field.item_fields) {
        return field.required ? [buildDefaultItem(field.item_fields)] : [];
      }
      return [];
    }
  }
  return null;
}

export function buildDefaultItem(
  itemFields: Record<string, TemplateField>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(itemFields)) {
    result[key] = buildDefaultField(field);
  }
  return result;
}

/**
 * Whether a field holds anything beyond its zero value. Measured against the
 * default rather than as "truthy": a boolean left `false` or a required
 * list's one empty row is not input. This is the wizard's "the user has typed
 * something" — it creates the draft, and it marks a section done in the jump
 * menu.
 */
export function fieldHasInput(field: TemplateField, value: unknown): boolean {
  return !isJsonEqual(value, buildDefaultField(field));
}

/** Whether any field of the form holds input — see `fieldHasInput`. */
export function formHasInput(schema: TemplateSchema, formData: Record<string, unknown>): boolean {
  return Object.entries(schema.fields ?? {}).some(([key, field]) =>
    fieldHasInput(field, formData[key])
  );
}

/**
 * Walk the schema and collect validation errors (required fields that are blank, etc.)
 * Returns a list of human-readable error paths like "client.services[0].name".
 */
export function collectValidationErrors(
  schema: TemplateSchema,
  formData: Record<string, unknown>
): string[] {
  const errors: string[] = [];
  for (const [key, field] of Object.entries(schema.fields ?? {})) {
    // Top-level fields see the root form data as their sole enclosing scope.
    validateField(field, key, formData[key], formData, [formData], errors);
  }
  return errors;
}

/**
 * Validate a single top-level section (one schema entry).
 * Uses the full formData as the condition root so cross-section conditions work.
 */
export function collectSectionErrors(
  sectionKey: string,
  field: TemplateField,
  formData: Record<string, unknown>
): string[] {
  const errors: string[] = [];
  validateField(field, sectionKey, formData[sectionKey], formData, [formData], errors);
  return errors;
}

/**
 * Evaluate a top-level field's condition against the current form data.
 * Returns true when no condition exists. Uses the shared `evaluateCondition`
 * so scoped lookups behave the same way as at every other level of the
 * schema tree.
 */
export function isTopLevelFieldVisible(
  field: TemplateField,
  formData: Record<string, unknown>
): boolean {
  // At the top level "local" = the root form data itself.
  return evaluateCondition(field.condition_ast, [formData], formData);
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function validateField(
  field: TemplateField,
  path: string,
  value: unknown,
  rootFormData: Record<string, unknown>,
  localScopes: unknown[],
  errors: string[]
): void {
  const conditionAst = field.condition_ast;
  if (conditionAst && !evaluateCondition(conditionAst, localScopes, rootFormData)) {
    return;
  }

  if (
    field.type === TemplateFieldType.OBJECT ||
    field.type === TemplateFieldType.LEGAL_ENTITY ||
    field.type === TemplateFieldType.NATURAL_PERSON
  ) {
    if (value === null) {
      if (field.required) errors.push(path);
      return;
    }
    const source = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
    const children = field.fields ?? {};
    for (const [childKey, childField] of Object.entries(children)) {
      // Children see THIS object as their innermost scope; outer scopes
      // (grandparent objects, enclosing list items, root) are still
      // reachable by walking the chain when inner lookup misses.
      validateField(
        childField,
        `${path}.${childKey}`,
        source[childKey],
        rootFormData,
        [source, ...localScopes],
        errors
      );
    }
    return;
  }

  if (field.type === TemplateFieldType.LIST) {
    const items = Array.isArray(value) ? value : [];
    if (field.required && items.length === 0) {
      errors.push(path);
      return;
    }
    if (field.item_fields) {
      for (const [index, item] of items.entries()) {
        const source = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
        for (const [childKey, childField] of Object.entries(field.item_fields ?? {})) {
          // Each list item is the innermost scope for its children.
          validateField(
            childField,
            `${path}[${index}].${childKey}`,
            source[childKey],
            rootFormData,
            [source, ...localScopes],
            errors
          );
        }
      }
    } else if (field.required) {
      for (const [index, item] of items.entries()) {
        if (isBlank(item)) errors.push(`${path}[${index}]`);
      }
    }
    return;
  }

  // A `default_value` makes the field optional even when `required: true` —
  // the template's `default(...)` filter will substitute at render time if
  // the user leaves it blank.
  if (field.required && isBlank(value) && !field.default_value) {
    errors.push(path);
  }
}

export function getCurrencyCode(field: TemplateField): string | null {
  const filter = (field.filters ?? []).find(f => f.name === 'format_currency');
  if (!filter) return null;
  const args = filter.args ?? [];
  return args.length > 0 ? args[0] : null;
}
